import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppState } from "../store";
import { onGet, onPost, onPatch, onDelete } from "../../core/api-service";
import { ErrorState, ResponseState } from "../common-state";
import paths from "../../navigation/paths";
import { setErrorState } from "./common-slice";

export enum CustomerStatus {
  Active = "active",
  Inactive = "inactive",
}

export interface UserResponse {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  misaCode: string;
  workingTimes: { startTime: string; endTime: string }[];
  leadGroupTeam: string[];
}

export interface CustomerResponse {
  id: string;
  name: string;
  email: string;
  status: CustomerStatus;
  users: UserResponse[];
}

export interface CustomerRequest {
  name: string;
  email?: string;
  status?: CustomerStatus;
  userIds?: string[];
}

export interface GetAllCustomersRequest {
  status: CustomerStatus[];
}

export interface CreateCustomerRequest extends CustomerRequest {}

export interface UpdateCustomerRequest extends CustomerRequest {
  id: string;
}

export interface GetCustomerByIdRequest {
  id: string;
}

interface CustomerState {
  createCustomerState: { data: ResponseState<CustomerResponse> | null; loading: boolean };
  customerByIdState: { data: CustomerResponse | null; loading: boolean };
  allCustomersState: { data: CustomerResponse[]; loading: boolean };
  updateCustomerState: { data: ResponseState<void> | null; loading: boolean };
  deleteCustomerState: { data: ResponseState<void> | null; loading: boolean };
}

export const createCustomerStateAction = createAsyncThunk(
  "customers/create",
  async (params: CreateCustomerRequest, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPost("/customers", params);
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const getCustomerByIdStateAction = createAsyncThunk(
  "customers/id",
  async (params: GetCustomerByIdRequest, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onGet(`/customers/${params.id}`);
      return response.data;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const allCustomersStateAction = createAsyncThunk(
  "customers/all",
  async (params: GetAllCustomersRequest, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPost("/customers/all", params);
      return response.data;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const updateCustomerStateAction = createAsyncThunk(
  "customers/update",
  async (params: UpdateCustomerRequest, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPatch(`/customers/${params.id}`, params);
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const deleteCustomerStateAction = createAsyncThunk(
  "customers/delete",
  async (params: GetCustomerByIdRequest, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onDelete(`/customers/${params.id}`);
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const customerSlice = createSlice({
  name: "customers",
  initialState: {
    createCustomerState: { data: null, loading: false },
    customerByIdState: { data: null, loading: false },
    allCustomersState: { data: [], loading: false },
    updateCustomerState: { data: null, loading: false },
    deleteCustomerState: { data: null, loading: false },
  } as CustomerState,
  reducers: {
    clearCustomerStates(state, _: PayloadAction) {
      state.createCustomerState = { data: null, loading: false };
      state.customerByIdState = { data: null, loading: false };
      state.allCustomersState = { data: [], loading: false };
      state.updateCustomerState = { data: null, loading: false };
      state.deleteCustomerState = { data: null, loading: false };
    },
    clearCreateCustomerState(state, _: PayloadAction) {
      state.createCustomerState = { data: null, loading: false };
    },
    clearUpdateCustomerState(state, _: PayloadAction) {
      state.updateCustomerState = { data: null, loading: false };
    },
    clearDeleteCustomerState(state, _: PayloadAction) {
      state.deleteCustomerState = { data: null, loading: false };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createCustomerStateAction.pending, (state: CustomerState, _) => {
        state.createCustomerState.loading = true;
        state.createCustomerState.data = null;
      })
      .addCase(createCustomerStateAction.fulfilled, (state: CustomerState, action) => {
        state.createCustomerState.loading = false;
        state.createCustomerState.data = action.payload as ResponseState<CustomerResponse>;
      })
      .addCase(createCustomerStateAction.rejected, (state: CustomerState, _) => {
        state.createCustomerState.loading = false;
      })
      .addCase(getCustomerByIdStateAction.pending, (state: CustomerState, _) => {
        state.customerByIdState.loading = true;
        state.customerByIdState.data = null;
      })
      .addCase(getCustomerByIdStateAction.fulfilled, (state: CustomerState, action) => {
        state.customerByIdState.loading = false;
        state.customerByIdState.data = action.payload as CustomerResponse;
      })
      .addCase(getCustomerByIdStateAction.rejected, (state: CustomerState, _) => {
        state.customerByIdState.loading = false;
      })
      .addCase(allCustomersStateAction.pending, (state: CustomerState, _) => {
        state.allCustomersState.loading = true;
        state.allCustomersState.data = [];
      })
      .addCase(allCustomersStateAction.fulfilled, (state: CustomerState, action) => {
        state.allCustomersState.loading = false;
        state.allCustomersState.data = action.payload as CustomerResponse[];
      })
      .addCase(allCustomersStateAction.rejected, (state: CustomerState, _) => {
        state.allCustomersState.loading = false;
      })
      .addCase(updateCustomerStateAction.pending, (state: CustomerState, _) => {
        state.updateCustomerState.loading = true;
        state.updateCustomerState.data = null;
      })
      .addCase(updateCustomerStateAction.fulfilled, (state: CustomerState, action) => {
        state.updateCustomerState.loading = false;
        state.updateCustomerState.data = action.payload as ResponseState<void>;
      })
      .addCase(updateCustomerStateAction.rejected, (state: CustomerState, _) => {
        state.updateCustomerState.loading = false;
      })
      .addCase(deleteCustomerStateAction.pending, (state: CustomerState, _) => {
        state.deleteCustomerState.loading = true;
        state.deleteCustomerState.data = null;
      })
      .addCase(deleteCustomerStateAction.fulfilled, (state: CustomerState, action) => {
        state.deleteCustomerState.loading = false;
        state.deleteCustomerState.data = action.payload as ResponseState<void>;
      })
      .addCase(deleteCustomerStateAction.rejected, (state: CustomerState, _) => {
        state.deleteCustomerState.loading = false;
      });
  },
});

export const {
  clearCustomerStates,
  clearCreateCustomerState,
  clearUpdateCustomerState,
  clearDeleteCustomerState,
} = customerSlice.actions;

export const getCreateCustomerState = (state: AppState) => state.customers.createCustomerState;
export const getCustomerByIdState = (state: AppState) => state.customers.customerByIdState;
export const getAllCustomersState = (state: AppState) => state.customers.allCustomersState;
export const getUpdateCustomerState = (state: AppState) => state.customers.updateCustomerState;
export const getDeleteCustomerState = (state: AppState) => state.customers.deleteCustomerState;

export default customerSlice.reducer;